import { getEnv } from '@xbcb/ui-utils';
import { UiStage } from '@xbcb/ui-types';
import { TagKey, StrategicBusinessUnitTagValue } from '@xbcb/shared-types';
import { CurrentUser } from 'libs/hooks/useCurrentUser';

export enum ClassificationUserType {
  CLASSIFICATION_EVALUATOR = 'CLASSIFICATION_EVALUATOR',
  EXTERNAL_EVALUATOR = 'EXTERNAL_EVALUATOR',
  NONE = 'NONE',
}

const hasBusinessSupportTeamTag = (
  currentUser: CurrentUser,
  tagValue: string,
): boolean => {
  const { businessSupportTeams } = currentUser;
  return !!businessSupportTeams?.nodes?.some(
    ({ tags }: { tags?: { key: string; value: string }[] }) =>
      tags?.some(
        ({ key, value }) =>
          key === TagKey.STRATEGIC_BUSINESS_UNIT && value === tagValue,
      ),
  );
};

export const getClassificationUserType = (
  currentUser: CurrentUser,
): ClassificationUserType => {
  if (
    hasBusinessSupportTeamTag(
      currentUser,
      StrategicBusinessUnitTagValue.CBMS_CLASSIFICATION_SME,
    )
  ) {
    return ClassificationUserType.CLASSIFICATION_EVALUATOR;
  }
  if (
    hasBusinessSupportTeamTag(
      currentUser,
      StrategicBusinessUnitTagValue.CBMS_CLASSIFICATION_EXTERNAL,
    )
  ) {
    return ClassificationUserType.EXTERNAL_EVALUATOR;
  }
  return ClassificationUserType.NONE;
};

/**
 * Returns true if the user has any classification access (CLASSIFICATION_EVALUATOR or EXTERNAL_EVALUATOR).
 * Used for menu visibility — both user types see the "Classification Evaluation" menu item.
 * In non-prod environments, returns true for all operators as a dev fallback.
 * @param {CurrentUser} currentUser - The current user object
 * @return {boolean} Whether the user has classification access
 */
export const hasClassificationAccess = (currentUser: CurrentUser): boolean => {
  const userType = getClassificationUserType(currentUser);
  if (userType !== ClassificationUserType.NONE) {
    return true;
  }

  // Non-prod fallback: allow all operators until BST tags are configured
  const { stage } = getEnv();
  if (stage !== UiStage.PROD) {
    return true;
  }

  return false;
};
